/**
 * FileExplorer — visor de carpetas y ficheros de la ZFT (GEN-22 #323, eje A).
 *
 * Sólo se llega aquí si el descriptor habilita el ítem de menú `ficheros`
 * (capabilities.menu, R-PT-02). Muestra EXCLUSIVAMENTE las cuatro zonas del
 * owner (ADR-020 §4.3 · eje A): Raw, Inbox, Outputs, Archive. Las zonas DIKW,
 * el Kernel, Digital Team y Workspace NO son navegables — el conocimiento se
 * consume a través de los agentes, no navegando el sustrato.
 *
 * Renderizador fino (ADR-020 §2): no toca S3 ni implementa lógica de ficheros.
 * Pide listar/subir/descargar por endpoints (api/client.js) y:
 *   - Inbox   → subir es directo (sin tutelaje): ficheros o una carpeta entera con sus
 *               subcarpetas (GEN-EKB-041), con el botón o arrastrándola.
 *   - Outputs → ver/descargar es directo.
 *   - Archive → ver/descargar directo, SOLO lectura (no sube).
 *   - Raw     → es material del owner (se lista), pero abrir un fichero pasa por
 *               el FLUJO TUTELADO (propose→approve, RT-09): nunca acceso directo.
 *
 * Recordatorio R-PT-04: ocultar/gates aquí es cosmético; el kernel revalida la
 * puerta del eje A y el grant de `raw` en cada endpoint.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { useSession } from '../context/SessionContext.jsx'
import { FolderOpen, FileText, Download, Upload, FolderUp, Lock, ChevronRight, ShieldCheck } from 'lucide-react'
import { listFiles, uploadInboxFile, downloadFile, proposeFileAction, getFilePolicy } from '../api/client.js'
import { ApiError } from '../api/errors.js'
import ActionPreviewEvent from './events/ActionPreviewEvent.jsx'
import CabeceraLanding from './CabeceraLanding.jsx'
import { useFotoAgente } from '../context/FotosAgentes.jsx'
import { arrastraFicheros, itemsDeDrop, itemsDeLista, subirItem } from '../lib/subidaCarpeta.js'

// Las CUATRO zonas del eje A (contrato ADR-020 §4.3). El front conoce este
// contrato estable; las affordances finas (subir/descargar/tutelado) las dirige
// el `vista` que devuelve el backend por zona (data-driven, R-PT-02).
const ZONES = [
  { id: 'raw',     label: 'Raw',     hint: 'Tu material privado en crudo. Abrir un fichero requiere tu confirmación explícita.' },
  { id: 'inbox',   label: 'Inbox',   hint: 'Sube aquí lo que quieras que el equipo procese.' },
  { id: 'outputs', label: 'Outputs', hint: 'Entregables generados por el equipo. Descárgalos cuando quieras.' },
  { id: 'archive', label: 'Archive', hint: 'Histórico archivado. Solo lectura.' },
]

const ERROR_MESSAGES = {
  forbidden:                'No tienes permiso para esta zona.',
  zona_no_navegable:        'Esta zona no es navegable desde el visor.',
  zona_desconocida:         'Esa zona no existe.',
  fichero_no_encontrado:    'El fichero ya no está disponible.',
  requiere_accion_tutelada: 'Este fichero privado requiere una acción tutelada aprobada.',
  ruta_invalida:            'Ruta no válida.',
}
const msg = err => ERROR_MESSAGES[err?.code] ?? err?.message ?? 'Ha ocurrido un error inesperado.'

function formatBytes(n) {
  if (n == null) return ''
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / 1024 / 1024).toFixed(1)} MB`
}

// Dispara la descarga del blob en el navegador. En entornos sin DOM (tests) es
// un no-op silencioso: la lógica probada es la llamada al cliente, no el click.
function triggerDownload(blob, filename) {
  try {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename || 'descarga'
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 0)
  } catch { /* sin DOM/URL (jsdom): la descarga real no aplica */ }
}

// --- Acceso tutelado a un fichero de `raw` (propose → approve → descarga) -----
function RawAccessDialog({ path, onClose }) {
  const [preview, setPreview] = useState(null)
  const [phase, setPhase]     = useState('proposing') // proposing | ready | error
  const [error, setError]     = useState(null)
  const [granted, setGranted] = useState(false)
  const [downloading, setDownloading] = useState(false)

  useEffect(() => {
    let vivo = true
    proposeFileAction('leer_raw', { path })
      .then(p => { if (vivo) { setPreview(p); setPhase('ready') } })
      .catch(err => { if (vivo) { setError(msg(err)); setPhase('error') } })
    return () => { vivo = false }
  }, [path])

  const handleDownload = useCallback(async () => {
    setDownloading(true)
    setError(null)
    try {
      const { blob, filename } = await downloadFile('raw', path, preview.action_id)
      triggerDownload(blob, filename)
    } catch (err) {
      setError(msg(err))
    } finally {
      setDownloading(false)
    }
  }, [path, preview])

  return (
    <div className="fe-modal-backdrop" onClick={onClose} role="dialog" aria-modal="true" aria-label="Acceso tutelado a fichero privado">
      <div className="fe-modal" onClick={e => e.stopPropagation()}>
        <header className="fe-modal__head">
          <span className="fe-modal__icon"><Lock size={16} strokeWidth={1.8} aria-hidden="true" /></span>
          <span className="fe-modal__title">Acceso tutelado</span>
        </header>
        <p className="fe-modal__path">raw / {path}</p>
        <p className="fe-modal__note">
          Es tu material privado. Para abrirlo debes aprobar explícitamente esta
          acción — no hay acceso directo.
        </p>

        {phase === 'proposing' && <p className="fe-state">Preparando la solicitud…</p>}
        {phase === 'error' && <p className="fe-state fe-state--error" role="alert">{error}</p>}
        {phase === 'ready' && preview && (
          <ActionPreviewEvent payload={preview} onApproved={() => setGranted(true)} />
        )}

        {granted && (
          <div className="fe-modal__granted">
            <button className="fe-btn fe-btn--primary" onClick={handleDownload} disabled={downloading}>
              <Download size={15} strokeWidth={1.8} aria-hidden="true" />
              {downloading ? 'Descargando…' : 'Descargar fichero'}
            </button>
            {error && <span className="fe-inline-error" role="alert">{error}</span>}
          </div>
        )}

        <div className="fe-modal__foot">
          <button className="fe-btn fe-btn--ghost" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  )
}

// GEN-PX-029: el encargo que el botón de la cabecera («Pídeselo a Jimmy») le manda a Jimmy.
const ENCARGO_APRENDER_INBOX = 'Aprende los ficheros de Inbox que aún no están en ningún KB.'

export default function FileExplorer({ onAbrirConversacion = null }) {
  const { capabilities, agents = [] } = useSession()
  const fotoDe = useFotoAgente()
  // Cabecera de Jimmy que emite el kernel en el ítem `ficheros` (solo el administrador lo ve); aquí solo se pinta.
  const cab = capabilities?.menu?.find(m => m?.id === 'ficheros')?.cabecera
  const ag = cab?.agente && (agents.find(a => a.id === cab.agente.id) ?? cab.agente)
  const destino = cab?.boton && agents.find(a => a.id === cab.boton.agente_id)
  const [zona, setZona]       = useState('outputs')
  const [path, setPath]       = useState('')
  const [data, setData]       = useState(null)   // { zona, path, vista, entries }
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState(null)
  const [rawTarget, setRawTarget]     = useState(null)  // path del fichero raw en acceso tutelado
  const [uploadBusy, setUploadBusy]   = useState(false)
  const [uploadMsg, setUploadMsg]     = useState(null)
  const [policy, setPolicy]           = useState(null)   // #322: política de gobierno (taxonomía/retención)
  const [arrastrando, setArrastrando] = useState(false)
  const fileInputRef = useRef(null)
  const carpetaInputRef = useRef(null)

  // Política de la ZFT (#322): se pide una vez. Es informativa (pinta pistas de
  // carpetas canónicas); si falla o viene vacía, el visor funciona igual.
  useEffect(() => {
    let vivo = true
    getFilePolicy().then(p => { if (vivo) setPolicy(p) }).catch(() => {})
    return () => { vivo = false }
  }, [])

  // Carpetas sugeridas por la taxonomía del tenant para la zona actual (vacío
  // hoy hasta que Cassie/Max la definan por config).
  const sugeridas = policy?.taxonomy?.[zona] ?? []

  const zoneMeta = ZONES.find(z => z.id === zona)
  const vista = data?.vista

  const load = useCallback(async (z, p) => {
    setLoading(true)
    setError(null)
    try {
      const res = await listFiles(z, p)
      setData(res)
    } catch (err) {
      setError(msg(err))
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load(zona, path) }, [zona, path, load])

  const selectZone = useCallback((z) => {
    setUploadMsg(null)
    setPath('')
    setZona(z)
  }, [])

  const enterDir = useCallback((nombre) => {
    setUploadMsg(null)
    setPath(p => (p ? `${p}/${nombre}` : nombre))
  }, [])

  const goCrumb = useCallback((p) => {
    setUploadMsg(null)
    setPath(p)
  }, [])

  const handleFile = useCallback(async (nombre) => {
    const full = path ? `${path}/${nombre}` : nombre
    if (vista === 'tutelado') {
      // Raw: nunca descarga directa — abre el flujo tutelado.
      setRawTarget(full)
      return
    }
    if (vista === 'lectura') {
      try {
        const { blob, filename } = await downloadFile(zona, full)
        triggerDownload(blob, filename)
      } catch (err) {
        setError(msg(err))
      }
    }
    // vista 'subida' (inbox): los ficheros no se descargan por el visor.
  }, [vista, zona, path])

  const onPickFile = useCallback(async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''  // permite re-subir el mismo nombre
    if (!file) return
    setUploadBusy(true)
    setUploadMsg(null)
    try {
      await uploadInboxFile(zona, file, path)
      setUploadMsg({ ok: true, text: `«${file.name}» subido a Inbox.` })
      await load(zona, path)
    } catch (err) {
      setUploadMsg({ ok: false, text: msg(err) })
    } finally {
      setUploadBusy(false)
    }
  }, [zona, path, load])

  // GEN-EKB-041 · subir una carpeta (elegida o arrastrada) a la carpeta actual de Inbox, conservando su nombre y subcarpetas.
  // Un fichero que falla no corta el resto: se sigue y al final se dice cuáles no subieron.
  const subirItems = useCallback(async (items) => {
    if (items.length === 0) return
    setUploadBusy(true)
    setUploadMsg(null)
    const fallidos = []
    let hechos = 0
    for (const it of items) {
      setUploadMsg({ ok: true, text: `Subiendo ${hechos + fallidos.length + 1} de ${items.length}…` })
      try {
        await subirItem(uploadInboxFile, zona, it, path)
        hechos += 1
      } catch (err) {
        fallidos.push({ ruta: it.ruta, motivo: msg(err) })
      }
    }
    const raiz = items[0].ruta.includes('/') ? items[0].ruta.split('/')[0] : null
    const todos = items.every(it => it.ruta.split('/')[0] === raiz)
    const que = raiz && todos ? `Carpeta «${raiz}»` : 'Los ficheros'
    setUploadMsg(fallidos.length === 0
      ? { ok: true, text: `${que} subida a Inbox: ${hechos} ${hechos === 1 ? 'fichero' : 'ficheros'}.`.replace('Los ficheros subida', 'Los ficheros subidos') }
      : { ok: false, text: `${que}: ${hechos} de ${items.length} subidos. No se han podido subir: ${fallidos.map(f => `«${f.ruta}»`).join(', ')}. ${fallidos[0].motivo}` })
    setUploadBusy(false)
    await load(zona, path)
  }, [zona, path, load])

  const onPickCarpeta = useCallback((e) => {
    const items = itemsDeLista(e.target.files)
    e.target.value = ''  // permite volver a elegir la misma carpeta
    subirItems(items)
  }, [subirItems])

  const puedeSoltar = vista === 'subida' && !uploadBusy
  const onDragOver = (e) => {
    if (!puedeSoltar || !arrastraFicheros(e.dataTransfer)) return
    e.preventDefault()
    setArrastrando(true)
  }
  const onDrop = async (e) => {
    if (!arrastraFicheros(e.dataTransfer)) return
    e.preventDefault()
    setArrastrando(false)
    if (!puedeSoltar) return
    try {
      await subirItems(await itemsDeDrop(e.dataTransfer))
    } catch {
      setUploadMsg({ ok: false, text: 'No se ha podido leer lo que has soltado. Prueba con «Subir carpeta».' })
    }
  }

  const crumbs = [
    { label: zoneMeta?.label ?? zona, path: '' },
    ...(path ? path.split('/').map((seg, i, arr) => ({ label: seg, path: arr.slice(0, i + 1).join('/') })) : []),
  ]

  return (
    <>
    {cab && (
      <CabeceraLanding
        agente={ag ? { nombre: cab.agente.nombre, rol: cab.agente.rol, foto: fotoDe?.(ag) } : null}
        titulo={cab.titulo}
        texto={cab.texto}
        boton={destino && onAbrirConversacion ? { etiqueta: cab.boton.etiqueta, onClick: () => onAbrirConversacion(ENCARGO_APRENDER_INBOX, destino) } : null}
      />
    )}
    <section className={'file-explorer' + (arrastrando ? ' file-explorer--arrastrando' : '')}
      onDragOver={onDragOver} onDragLeave={() => setArrastrando(false)} onDrop={onDrop}>
      <header className="fe-head">
        <h2 className="fe-title">Ficheros</h2>
        <p className="fe-subtitle">Tu espacio de ficheros: material privado, entradas, entregables y archivo.</p>
      </header>

      {/* Selector de zona (solo las cuatro del eje A) */}
      <nav className="fe-zones" aria-label="Zonas de ficheros">
        {ZONES.map(z => (
          <button
            key={z.id}
            className={'fe-zone' + (z.id === zona ? ' fe-zone--active' : '')}
            onClick={() => selectZone(z.id)}
            aria-current={z.id === zona ? 'page' : undefined}
          >
            {z.id === 'raw' ? <Lock size={15} strokeWidth={1.8} aria-hidden="true" /> : <FolderOpen size={15} strokeWidth={1.8} aria-hidden="true" />}
            {z.label}
          </button>
        ))}
      </nav>

      {/* Banda de contexto de la zona: pista + insignias (tutelado / solo lectura) */}
      <div className="fe-zonebar">
        <span className="fe-zonebar__hint">{zoneMeta?.hint}</span>
        <span className="fe-zonebar__badges">
          {vista === 'tutelado' && (
            <span className="fe-badge fe-badge--tutelado"><Lock size={12} strokeWidth={2} aria-hidden="true" /> Material privado · acceso tutelado</span>
          )}
          {zona === 'archive' && (
            <span className="fe-badge fe-badge--ro"><ShieldCheck size={12} strokeWidth={2} aria-hidden="true" /> Solo lectura</span>
          )}
          {vista === 'subida' && (
            <button className="fe-btn fe-btn--primary fe-upload-btn" onClick={() => fileInputRef.current?.click()} disabled={uploadBusy}>
              <Upload size={15} strokeWidth={1.8} aria-hidden="true" />
              {uploadBusy ? 'Subiendo…' : 'Subir fichero'}
            </button>
          )}
          {vista === 'subida' && (
            <button className="fe-btn fe-btn--primary fe-upload-btn" onClick={() => carpetaInputRef.current?.click()} disabled={uploadBusy}>
              <FolderUp size={15} strokeWidth={1.8} aria-hidden="true" />
              Subir carpeta
            </button>
          )}
        </span>
        <input ref={fileInputRef} type="file" className="fe-file-input" onChange={onPickFile} aria-hidden="true" tabIndex={-1} />
        <input ref={carpetaInputRef} type="file" webkitdirectory="" className="fe-file-input fe-carpeta-input" onChange={onPickCarpeta} aria-hidden="true" tabIndex={-1} />
      </div>

      {uploadMsg && (
        <p className={'fe-upload-msg' + (uploadMsg.ok ? ' fe-upload-msg--ok' : ' fe-upload-msg--error')} role={uploadMsg.ok ? 'status' : 'alert'}>
          {uploadMsg.text}
        </p>
      )}

      {/* Carpetas sugeridas por la taxonomía del tenant (#322). Solo si la
          política las define; vacía por defecto → no se pinta nada. */}
      {sugeridas.length > 0 && !path && (
        <p className="fe-taxonomy-hint">
          Carpetas sugeridas: {sugeridas.join(' · ')}
        </p>
      )}

      {/* Breadcrumb */}
      <div className="fe-breadcrumb">
        {crumbs.map((c, i) => (
          <span key={c.path} className="fe-crumb">
            {i > 0 && <ChevronRight size={13} strokeWidth={1.8} aria-hidden="true" className="fe-crumb__sep" />}
            <button className="fe-crumb__btn" onClick={() => goCrumb(c.path)} disabled={i === crumbs.length - 1}>
              {c.label}
            </button>
          </span>
        ))}
      </div>

      {/* Listado */}
      {loading && <p className="fe-state">Cargando…</p>}
      {error && !loading && (
        <div className="fe-state fe-state--error" role="alert">
          <p>{error}</p>
          <button className="fe-btn fe-btn--ghost" onClick={() => load(zona, path)}>Reintentar</button>
        </div>
      )}
      {!loading && !error && data && (
        data.entries.length === 0 ? (
          <p className="fe-state">Esta carpeta está vacía.</p>
        ) : (
          <ul className="fe-list">
            {data.entries.map(entry => (
              <li key={entry.nombre} className="fe-row">
                {entry.tipo === 'dir' ? (
                  <button className="fe-item fe-item--dir" onClick={() => enterDir(entry.nombre)}>
                    <FolderOpen size={17} strokeWidth={1.7} aria-hidden="true" className="fe-item__ico" />
                    <span className="fe-item__name">{entry.nombre}</span>
                  </button>
                ) : vista === 'subida' ? (
                  // Inerte: no es un botón (un botón desactivado no deja seleccionar su texto) para poder copiar el nombre.
                  <div className="fe-item fe-item--file fe-item--inert">
                    <FileText size={17} strokeWidth={1.7} aria-hidden="true" className="fe-item__ico" />
                    <span className="fe-item__name">{entry.nombre}</span>
                    <span className="fe-item__meta">{formatBytes(entry.tamano)}</span>
                  </div>
                ) : (
                  <button
                    className="fe-item fe-item--file"
                    onClick={() => handleFile(entry.nombre)}
                    title={vista === 'tutelado' ? 'Requiere acceso tutelado' : vista === 'lectura' ? 'Descargar' : undefined}
                  >
                    <FileText size={17} strokeWidth={1.7} aria-hidden="true" className="fe-item__ico" />
                    <span className="fe-item__name">{entry.nombre}</span>
                    <span className="fe-item__meta">{formatBytes(entry.tamano)}</span>
                    <span className="fe-item__action">
                      {vista === 'tutelado' && <Lock size={14} strokeWidth={1.8} aria-hidden="true" />}
                      {vista === 'lectura' && <Download size={14} strokeWidth={1.8} aria-hidden="true" />}
                    </span>
                  </button>
                )}
              </li>
            ))}
          </ul>
        )
      )}

      {rawTarget && <RawAccessDialog path={rawTarget} onClose={() => setRawTarget(null)} />}
    </section>
    </>
  )
}
